import * as React from "react";
import { ChevronDownIcon } from "@radix-ui/react-icons";
import {
  SortingState,
  ColumnFiltersState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useQuery } from "@tanstack/react-query";
import { fetchCompanies } from "@/services/companyManagement";
import { useState } from "react";
import { companiesTableColumns, CompanyTableData } from "./companies-table-columns";
import { PaginatedResponse } from "@/shared/models/pagination.model";
import { Skeleton } from "@/components/ui/skeleton";
import { PaginationInfo } from "@/components/pagination-info";
import { CompaniesFilterDialog, FilterValues } from "./companies-filter-dialog";
import { useCompanyFilters } from "../hooks/useCompanyFilters";

interface CompanyApiResponse {
  _id: string;
  name: string;
  companyAdmin?: {
    name?: string;
    email?: string;
  };
  address?: string;
  phone?: string;
  createdAt: string;
  updatedAt: string;
}

interface CompaniesTableProps {
  onEdit: (company: CompanyTableData) => void;
  onDelete: (company: CompanyTableData) => void;
}

function CompaniesTable({ onEdit, onDelete }: CompaniesTableProps) {
  const { filters, updateFilters, updateFilter, clearFilters } =
    useCompanyFilters();

  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  );
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});

  const companyData = useQuery<PaginatedResponse>({
    queryKey: ["companies", filters],
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    queryFn: async () => {
      // Convert filters to API parameters
      const apiParams = { ...filters };

      let response = await fetchCompanies(apiParams);

      response = {
        ...response,
        data: response?.data?.map((company: CompanyApiResponse) => ({
          id: company._id,
          name: company.name,
          companyAdmin: company.companyAdmin?.name || company.companyAdmin?.email || null,
          address: company.address,
          phone: company.phone,
          createdAt: company.createdAt,
          updatedAt: company.updatedAt,
        })),
      };

      return response;
    },
  });

  // Handle filter changes from the dialog
  const handleFilterChange = (newFilters: FilterValues) => {
    updateFilters(newFilters);
  };

  const table = useReactTable({
    data: companyData.data?.data || [],
    columns: companiesTableColumns({ onEdit, onDelete }),
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  });

  if (companyData.isLoading) {
    return (
      <div className="w-full">
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Company Name</TableHead>
                <TableHead>Company Admin</TableHead>
                <TableHead>Address</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Created At</TableHead>
                <TableHead>Updated At</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Skeleton className="h-4 w-[200px]" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-[150px]" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-[200px]" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-[100px]" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-[120px]" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-[120px]" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-[50px]" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  }

  if (companyData.error) {
    return (
      <div className="w-full">
        <div className="rounded-md border p-4">
          <p className="text-red-600">
            Error loading companies: {companyData.error.message}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="flex items-center py-4">
        <Input
          placeholder="Filter companies by name..."
          value={(table.getColumn("name")?.getFilterValue() as string) ?? ""}
          onChange={(event) =>
            table.getColumn("name")?.setFilterValue(event.target.value)
          }
          className="max-w-sm"
        />
        <CompaniesFilterDialog
          filters={filters}
          onFiltersChange={handleFilterChange}
          onClearFilters={clearFilters}
        />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="ml-auto">
              Columns <ChevronDownIcon className="ml-2 h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {table
              .getAllColumns()
              .filter((column) => column.getCanHide())
              .map((column) => {
                return (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    className="capitalize"
                    checked={column.getIsVisible()}
                    onCheckedChange={(value) =>
                      column.toggleVisibility(!!value)
                    }
                  >
                    {column.id}
                  </DropdownMenuCheckboxItem>
                );
              })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={companiesTableColumns({ onEdit, onDelete }).length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-end space-x-2 py-4">
        <PaginationInfo
          currentPage={companyData.data?.paging?.currentPage || 1}
          totalPages={companyData.data?.paging?.totalPages || 1}
          totalItems={companyData.data?.paging?.totalItems || 0}
          label="companies"
        />
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => updateFilter("page", (companyData.data?.paging?.currentPage || 1) - 1)}
            disabled={!companyData.data?.paging?.currentPage || companyData.data.paging.currentPage <= 1}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => updateFilter("page", (companyData.data?.paging?.currentPage || 1) + 1)}
            disabled={!companyData.data?.paging?.currentPage || companyData.data.paging.currentPage >= (companyData.data?.paging?.totalPages || 1)}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}

export default CompaniesTable;
